import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * <custom-combo-builder>
 *
 * Build-your-own pack builder.
 *
 * Rather than selling a placeholder bundle product, this adds the chosen number
 * of real single-trunk variants to the cart. That means stock is drawn from the
 * exact colour and size the shopper picked, the warehouse packs against real
 * SKUs, and nothing can be oversold. The pack price is produced by an automatic
 * quantity discount on the single trunk.
 *
 * If the script fails, the page still has a working native form for the pack
 * product with the chosen combo recorded as line-item properties, so an order
 * is never silently lost — it just doesn't draw down the singles.
 */
class CustomComboBuilder extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onSwatchClick);
    this.addEventListener('change', this.#onSizeChange);
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#detachSubmit();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#init();
  }

  #init() {
    this.variants = this.#readVariantMap();
    this.#attachSubmit();
    this.#sync();
  }

  #readVariantMap() {
    const node = this.querySelector('script[data-variant-map]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[combo-builder] bad variant map', error);
      return {};
    }
  }

  get #form() {
    const scope = this.closest('.shopify-section') || document;
    return scope.querySelector('form[action*="/cart/add"]');
  }

  #attachSubmit() {
    this.#detachSubmit();
    const form = this.#form;
    if (!form) return;
    this.boundForm = form;

    // The theme's add-to-cart button runs its own click handler rather than
    // submitting the form, so intercept the click on the document in capture
    // phase — that runs before any handler bound further down the tree. The
    // submit listener is a belt-and-braces fallback.
    document.addEventListener('click', this.#onAddToCartClick, true);
    form.addEventListener('submit', this.#onSubmit, true);
  }

  #detachSubmit() {
    document.removeEventListener('click', this.#onAddToCartClick, true);
    this.boundForm?.removeEventListener('submit', this.#onSubmit, true);
    this.boundForm = null;
  }

  #onAddToCartClick = (event) => {
    const button = event.target.closest?.(
      '[ref="addToCartButton"], .add-to-cart-button, button[type="submit"], button[name="add"]'
    );
    if (!button || !this.boundForm?.contains(button)) return;
    this.#onSubmit(event);
  };

  #onSwatchClick = (event) => {
    const swatch = event.target.closest('[data-combo-swatch]');
    if (!swatch || !this.contains(swatch)) return;
    event.preventDefault();
    if (swatch.dataset.unavailable === 'true') return;

    const row = swatch.closest('[data-piece]');
    if (!row) return;
    row.querySelectorAll('[data-combo-swatch]').forEach((s) => s.setAttribute('aria-checked', 'false'));
    swatch.setAttribute('aria-checked', 'true');
    this.#sync();
  };

  #onSizeChange = (event) => {
    if (!event.target.matches('[data-combo-size]')) return;
    this.#sync();
  };

  /** Current selection for each row. */
  get selection() {
    return Array.from(this.querySelectorAll('[data-piece]')).map((row) => {
      const swatch =
        row.querySelector('[data-combo-swatch][aria-checked="true"]') || row.querySelector('[data-combo-swatch]');
      const select = row.querySelector('[data-combo-size]');
      return {
        row,
        colour: swatch?.dataset.colourName ?? '',
        size: select?.value ?? '',
      };
    });
  }

  /**
   * Reflect availability on the swatches and keep the no-JS fallback
   * properties in step with what's on screen.
   */
  #sync() {
    this.selection.forEach(({ row, colour, size }) => {
      row.querySelectorAll('[data-combo-swatch]').forEach((swatch) => {
        const entry = this.variants[`${size}|${swatch.dataset.colourName}`];
        swatch.dataset.unavailable = entry && entry.available ? 'false' : 'true';
      });

      const input = row.querySelector('input[data-combo-prop]');
      if (input) input.value = `${colour} / ${size}`;
    });

    this.#setError('');
  }

  #setError(message) {
    const el = this.querySelector('[data-combo-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  #onSubmit = async (event) => {
    const items = [];
    const unavailable = [];
    const packId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

    for (const { colour, size } of this.selection) {
      const entry = this.variants[`${size}|${colour}`];
      if (!entry || !entry.available) {
        unavailable.push(`${colour} / ${size}`);
        continue;
      }
      items.push({
        id: entry.id,
        quantity: 1,
        properties: {
          // Underscore-prefixed, so these are hidden from the cart and checkout
          // but still reach the order in admin for the warehouse to pack against.
          // _PackId is unique per add, which is what lets the cart template group
          // this pack's lines under one heading and keeps two separate packs of
          // the same colours from merging into each other.
          _Pack: this.dataset.packLabel || 'Build your own',
          _PackId: packId,
        },
      });
    }

    // Nothing usable — let the native form proceed rather than block the sale.
    if (!items.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (unavailable.length) {
      this.#setError(`Sold out: ${unavailable.join(', ')}`);
      return;
    }

    const button = this.boundForm?.querySelector('[ref="addToCartButton"], button[type="submit"]');
    button?.setAttribute('disabled', '');

    try {
      const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || body.message || 'Could not add to cart');
      }

      // Let the theme refresh the cart drawer/bubble.
      document.dispatchEvent(new CartAddEvent({}, String(items[0].id), { didError: false }));
      document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate, { bubbles: true, detail: { data: {} } }));
    } catch (error) {
      console.error('[combo-builder]', error);
      this.#setError(error.message || 'Could not add to cart');
    } finally {
      button?.removeAttribute('disabled');
    }
  };
}

if (!customElements.get('custom-combo-builder')) {
  customElements.define('custom-combo-builder', CustomComboBuilder);
}
